import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { FiArrowLeft, FiCreditCard, FiLock, FiCheck, FiAlertCircle } from 'react-icons/fi';

const CardPaymentPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Card form fields
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [installments, setInstallments] = useState(1);
  const [docType, setDocType] = useState('CPF');
  const [docNumber, setDocNumber] = useState('');

  // Mercado Pago
  const [mpReady, setMpReady] = useState(false);
  const [mpInstance, setMpInstance] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadOrder();
    loadMercadoPago();
  }, [orderId, user]);

  const loadOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
      
      if (response.data.payment_status === 'paid') {
        navigate(`/cliente/pedido/${orderId}`);
      }
    } catch (err) {
      setError('Pedido não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const loadMercadoPago = async () => {
    try {
      // Load Mercado Pago SDK
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = async () => {
        try {
          const configResponse = await api.get('/config/mercadopago');
          const publicKey = configResponse.data.public_key;
          
          if (publicKey && window.MercadoPago) {
            const mp = new window.MercadoPago(publicKey);
            setMpInstance(mp);
            setMpReady(true);
          }
        } catch (err) {
          console.error('Erro ao configurar Mercado Pago:', err);
        }
      };
      document.body.appendChild(script);
    } catch (err) {
      console.error('Erro ao carregar SDK:', err);
    }
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})/g, '$1 ').trim();
    return formatted.substring(0, 19);
  };

  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setProcessing(true);

    try {
      if (!mpInstance) {
        throw new Error('Mercado Pago não está pronto');
      }

      // Create card token
      const [expiryMonth, expiryYear] = expiryDate.split('/');
      
      const cardData = {
        cardNumber: cardNumber.replace(/\s/g, ''),
        cardholderName: cardName,
        cardExpirationMonth: expiryMonth,
        cardExpirationYear: '20' + expiryYear,
        securityCode: cvv,
        identificationType: docType,
        identificationNumber: docNumber.replace(/\D/g, '')
      };

      const cardToken = await mpInstance.createCardToken(cardData);
      
      if (!cardToken.id) {
        throw new Error('Erro ao tokenizar cartão');
      }

      // Send payment to backend
      const response = await api.post(`/payments/card/${orderId}?card_token=${cardToken.id}&installments=${installments}`);
      
      if (response.data.status === 'paid' || response.data.mp_status === 'approved') {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/cliente/pedido/${orderId}`);
        }, 2000);
      } else {
        setError(response.data.message || 'Pagamento em processamento');
      }
    } catch (err) {
      console.error('Erro no pagamento:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheck className="text-4xl text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Pagamento Aprovado!</h2>
          <p className="text-gray-600">Redirecionando para acompanhar seu pedido...</p>
        </div>
      </div>
    );
  }

  const installmentOptions = [];
  if (order) {
    for (let i = 1; i <= 12; i++) {
      const value = order.total_value / i;
      if (value >= 5) { // Mínimo de R$ 5 por parcela
        installmentOptions.push({
          value: i,
          label: i === 1 ? `1x de ${formatCurrency(value)} (sem juros)` : `${i}x de ${formatCurrency(value)}`
        });
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#DC143C] to-[#B01030] text-white sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full">
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="text-lg font-semibold">Pagamento com Cartão</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4">
        {/* Order Summary */}
        {order && (
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-2">Resumo do Pedido</h3>
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Pedido</span>
              <span>#{orderId.substring(0, 8)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="text-[#DC143C]">{formatCurrency(order.total_value)}</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Card Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-4 text-gray-700">
            <FiCreditCard className="text-xl" />
            <span className="font-semibold">Dados do Cartão</span>
            <FiLock className="text-green-600 ml-auto" />
          </div>

          <div className="space-y-4">
            {/* Card Number */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Número do Cartão</label>
              <input
                type="text"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                placeholder="0000 0000 0000 0000"
                maxLength={19}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                required
              />
            </div>

            {/* Card Name */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Nome no Cartão</label>
              <input
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value.toUpperCase())}
                placeholder="NOME COMO ESTÁ NO CARTÃO"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                required
              />
            </div>

            {/* Expiry and CVV */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Validade</label>
                <input
                  type="text"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                  placeholder="MM/AA"
                  maxLength={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">CVV</label>
                <input
                  type="text"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
                  placeholder="123"
                  maxLength={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Document */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Documento</label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                >
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Número</label>
                <input
                  type="text"
                  value={docNumber}
                  onChange={(e) => setDocNumber(e.target.value)}
                  placeholder={docType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Installments */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Parcelas</label>
              <select
                value={installments}
                onChange={(e) => setInstallments(parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
              >
                {installmentOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={processing || !mpReady}
            className="w-full mt-6 py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <FiLock />
                Pagar {order && formatCurrency(order.total_value)}
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            🔒 Pagamento seguro processado pelo Mercado Pago
          </p>
        </form>
      </main>
    </div>
  );
};

export default CardPaymentPage;
